/**
 * Authentication configuration for the application
 * Supports both subscription keys (API keys) and user authentication (email-based)
 * TODO: Replace with Entra ID (Azure B2C) authentication when configured
 */

import { RoleIds } from '@/types/roles'

/**
 * Authentication method type
 */
export type AuthMethod = 'subscription-key' | 'user-email'

/**
 * User authentication interface (mimics JWT auth)
 */
export interface UserAuth {
  email: string
  displayName: string
  roleIds: number[]
  description: string
  authMethod: 'user-email'
}

/**
 * Legacy subscription key interface
 * @deprecated Use role IDs from RoleIds instead
 */
export interface SubscriptionKey {
  key: string
  role: 'Viewer' | 'Contributor' | 'Administrator'
  roleId: number
  description: string
  authMethod: 'subscription-key'
}

/**
 * Combined auth config type
 */
export type AuthConfig = SubscriptionKey | UserAuth

/**
 * Available subscription keys for development/testing
 * These keys are configured in the backend appsettings.Development.json
 */
export const SUBSCRIPTION_KEYS: Record<string, SubscriptionKey> = {
  viewer: {
    key: 'demo-viewer-123',
    role: 'Viewer',
    roleId: RoleIds.Viewer,
    description: 'Read-only access to all configurations',
    authMethod: 'subscription-key'
  },
  contributor: {
    key: 'demo-contributor-456',
    role: 'Contributor',
    roleId: RoleIds.Contributor,
    description: 'Read and write access to presets and tenants',
    authMethod: 'subscription-key'
  },
  // identityAdmin: {
  //   key: 'demo-identityadmin-321',
  //   role: 'Administrator', // Legacy mapping
  //   roleId: RoleIds.IdentityAdmin,
  //   description: 'Manage user roles and identity configurations',
  //   authMethod: 'subscription-key'
  // },
  administrator: {
    key: '123',
    role: 'Administrator',
    roleId: RoleIds.Administrator,
    description: 'Full administrative access to all resources',
    authMethod: 'subscription-key'
  }
} as const

/**
 * Development users (mimics JWT authentication)
 * These users are seeded in the database (03-seed-development.sql)
 */
export const DEV_USERS: Record<string, UserAuth> = {
  admin: {
    email: 'admin@dev.local',
    displayName: 'Development Administrator',
    roleIds: [RoleIds.Administrator],
    description: 'Full administrative access',
    authMethod: 'user-email'
  },
  contributor: {
    email: 'contributor@dev.local',
    displayName: 'Development Contributor',
    roleIds: [RoleIds.Contributor],
    description: 'Read and write access to presets and tenants',
    authMethod: 'user-email'
  },
  viewer: {
    email: 'viewer@dev.local',
    displayName: 'Development Viewer',
    roleIds: [RoleIds.Viewer],
    description: 'Read-only access to all configurations',
    authMethod: 'user-email'
  },
  tenantAdmin: {
    email: 'tenantadmin@dev.local',
    displayName: 'Development Tenant Admin',
    roleIds: [RoleIds.TenantAdmin],
    description: 'Manage tenant configurations',
    authMethod: 'user-email'
  },
  licenceAdmin: {
    email: 'licenceadmin@dev.local',
    displayName: 'Development Licence Admin',
    roleIds: [RoleIds.LicenceAdmin],
    description: 'Manage licensing configurations',
    authMethod: 'user-email'
  },
  dbAdmin: {
    email: 'dbadmin@dev.local',
    displayName: 'Development Database Admin',
    roleIds: [RoleIds.DatabaseAdmin],
    description: 'Manage database configurations',
    authMethod: 'user-email'
  },
  presetAdmin: {
    email: 'presetadmin@dev.local',
    displayName: 'Development Preset Admin',
    roleIds: [RoleIds.PresetAdmin],
    description: 'Manage preset configurations',
    authMethod: 'user-email'
  },
  identityAdmin: {
    email: 'identityadmin@dev.local',
    displayName: 'Development Identity Admin',
    roleIds: [RoleIds.IdentityAdmin],
    description: 'Manage identity configurations',
    authMethod: 'user-email'
  },
  multiRole: {
    email: 'multiRole@dev.local',
    displayName: 'Multi-Role User',
    roleIds: [RoleIds.Contributor, RoleIds.LicenceAdmin],
    description: 'Contributor + Licence Admin (multi-role example)',
    authMethod: 'user-email'
  },
  specialist: {
    email: 'specialist@dev.local',
    displayName: 'Specialist',
    roleIds: [RoleIds.DatabaseAdmin, RoleIds.IdentityAdmin],
    description: 'Database + Identity Admin (multi-role specialist)',
    authMethod: 'user-email'
  }
} as const

/**
 * Default auth config for development
 * Using Contributor subscription key for development convenience
 */
export const DEFAULT_AUTH_CONFIG: AuthConfig = SUBSCRIPTION_KEYS.contributor

/**
 * Get all available auth configs for selection (both subscription keys and users)
 */
export function getAvailableAuthConfigs(): AuthConfig[] {
  return [
    ...Object.values(SUBSCRIPTION_KEYS),
    ...Object.values(DEV_USERS)
  ]
}

/**
 * Get all available subscription keys for selection
 * @deprecated Use getAvailableAuthConfigs for both keys and users
 */
export function getAvailableKeys(): SubscriptionKey[] {
  return Object.values(SUBSCRIPTION_KEYS)
}

/**
 * Get the currently active subscription key (for API key authentication)
 * Can be overridden by localStorage or environment configuration
 */
export function getSubscriptionKey(role?: 'Viewer' | 'Contributor' | 'Administrator'): string {
  // Check localStorage for user preference
  const storedKey = localStorage.getItem('subscription-key')
  if (storedKey) {
    return storedKey
  }
  
  // If role is specified, find matching key
  if (role) {
    const keyEntry = Object.values(SUBSCRIPTION_KEYS).find(k => k.role === role)
    return keyEntry?.key || (DEFAULT_AUTH_CONFIG as SubscriptionKey).key
  }
  
  // Return default
  return (DEFAULT_AUTH_CONFIG as SubscriptionKey).key
}

/**
 * Set the active subscription key
 * Stored in localStorage for persistence across page reloads
 */
export function setSubscriptionKey(key: string): void {
  localStorage.setItem('subscription-key', key)
}

/**
 * Clear the stored subscription key
 * Reverts to default key
 */
export function clearSubscriptionKey(): void {
  localStorage.removeItem('subscription-key')
}

/**
 * Get subscription key by role ID
 */
export function getKeyByRoleId(roleId: number): string | undefined {
  const keyEntry = Object.values(SUBSCRIPTION_KEYS).find(k => k.roleId === roleId)
  return keyEntry?.key
}

/**
 * Get user auth config by email
 */
export function getUserByEmail(email: string): UserAuth | undefined {
  return Object.values(DEV_USERS).find(u => u.email === email)
}
